import React, { useState } from "react";
import { Phone, Mail, MapPin, Building2, Clock, ExternalLink, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { apiClient, formatApiError } from "../lib/api";
import Reveal from "./Reveal";

// TODO: uzupełnić przez klienta
const PHONE_DISPLAY = "+48 669 255 184";
const PHONE_TEL = "+48669255184";
const EMAIL = "kontakt@agentbieszczady.pl";
const ADDRESS_LINE_1 = "plac Konstytucji 3 Maja 7";
const ADDRESS_LINE_2 = "38-600 Lesko";
const OPENING_HOURS = "Poniedziałek – Piątek";
const OPENING_HOURS_TIME = "8:00 – 16:00";
const GOOGLE_BUSINESS_URL = "https://maps.google.com/"; // TODO: uzupełnić linkiem do wizytówki Google

const SUBJECTS = [
  { value: "komunikacyjne", label: "Ubezpieczenie komunikacyjne (OC/AC)" },
  { value: "zyciowe", label: "Ubezpieczenie na życie" },
  { value: "majatkowe", label: "Ubezpieczenie domu / mieszkania" },
  { value: "nnw", label: "NNW" },
  { value: "firmowe", label: "Ubezpieczenie firmy" },
  { value: "inne", label: "Inne" },
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.subject || !form.message) {
      toast.error("Uzupełnij wszystkie pola formularza");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Wiadomość wysłana. Oddzwonię najszybciej, jak to możliwe.");
      setForm({ name: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Nie udało się wysłać wiadomości");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-20 md:py-28 bg-cream"
    >
      <div className="container-narrow">
        <Reveal>
          <div className="max-w-2xl mb-12">
            <div className="text-mustard-dark font-heading font-bold uppercase text-sm tracking-wider mb-3">
              Kontakt
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest leading-tight">
              Porozmawiajmy o Twoim ubezpieczeniu.
            </h2>
            <p className="mt-5 text-ink-muted text-lg leading-relaxed">
              Zadzwoń lub zostaw kontakt. Odpowiem na Twoje pytania,
              przedstawię dostępne opcje i pomogę podjąć decyzję,
              z którą będziesz czuć się pewnie.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Lewa kolumna: dane kontaktowe */}
          <div className="lg:col-span-5 space-y-6">
            <Reveal>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-phone-link"
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-bordersoft hover:border-forest/25 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-forest" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-forest">Telefon</div>
                  {/* TODO: uzupełnić przez klienta */}
                  <div className="text-ink-muted text-base mt-0.5">{PHONE_DISPLAY}</div>
                </div>
              </a>
            </Reveal>

            <Reveal delay={80}>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-bordersoft hover:border-forest/25 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-forest" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-forest">E-mail</div>
                  {/* TODO: uzupełnić przez klienta */}
                  <div className="text-ink-muted text-base mt-0.5 break-all">{EMAIL}</div>
                </div>
              </a>
            </Reveal>

            <Reveal delay={160}>
              <div
                data-testid="contact-address"
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-bordersoft"
              >
                <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center flex-shrink-0">
                  <Building2 size={20} className="text-forest" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-forest">Adres biura</div>
                  <div className="text-ink-muted text-base mt-0.5">
                    {ADDRESS_LINE_1}<br />
                    {ADDRESS_LINE_2}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div
                data-testid="contact-hours"
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-bordersoft"
              >
                <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-forest" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-forest">Godziny otwarcia</div>
                  <div className="text-ink-muted text-base mt-0.5">
                    {OPENING_HOURS}<br />
                    <span className="text-forest font-medium">{OPENING_HOURS_TIME}</span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div
                data-testid="contact-area"
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-bordersoft"
              >
                <div className="w-11 h-11 rounded-lg bg-forest/8 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-forest" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-forest">Obszar działania</div>
                  <div className="text-ink-muted text-base mt-0.5">
                    Bieszczady i całe Podkarpacie
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Mapa placeholder */}
            <Reveal delay={280}>
              <div
                data-testid="contact-map-placeholder"
                className="relative aspect-[16/10] rounded-xl overflow-hidden border border-bordersoft bg-forest/5"
              >
                <iframe
                  title="Mapa biura w Lesku"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=22.32%2C49.46%2C22.37%2C49.49&amp;layer=mapnik&amp;marker=49.4711%2C22.3306"
                  className="w-full h-full"
                  style={{ border: 0 }}
                  loading="lazy"
                />
                <a
                  href={GOOGLE_BUSINESS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-business-link"
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-forest text-sm font-semibold px-3 py-2 rounded-full border border-bordersoft hover:bg-white transition-colors"
                >
                  Wizytówka Google
                  <ExternalLink size={14} />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Prawa kolumna: formularz */}
          <div className="lg:col-span-7">
            <Reveal>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-white rounded-2xl p-6 md:p-8 border border-bordersoft space-y-5"
              >
                <div>
                  <Label htmlFor="name" className="text-forest font-semibold mb-2 block">
                    Imię
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-form-name"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Jak masz na imię?"
                    className="h-12 text-base bg-cream border-bordersoft focus-visible:ring-forest"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-forest font-semibold mb-2 block">
                    Telefon
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="contact-form-phone"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="+48 ..."
                    className="h-12 text-base bg-cream border-bordersoft focus-visible:ring-forest"
                    required
                  />
                </div>

                <div>
                  <Label className="text-forest font-semibold mb-2 block">
                    Temat
                  </Label>
                  <Select
                    value={form.subject}
                    onValueChange={(v) => setForm((p) => ({ ...p, subject: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-form-subject"
                      className="h-12 text-base bg-cream border-bordersoft focus:ring-forest"
                    >
                      <SelectValue placeholder="Wybierz temat" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="text-base">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-forest font-semibold mb-2 block">
                    Wiadomość
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-form-message"
                    value={form.message}
                    onChange={handleChange("message")}
                    rows={5}
                    placeholder="Napisz krótko, czego potrzebujesz..."
                    className="text-base bg-cream border-bordersoft focus-visible:ring-forest resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-mustard hover:bg-mustard-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-base transition-colors min-h-[52px]"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
                </button>

                <p className="text-sm text-ink-muted text-center">
                  Wysyłając wiadomość, wyrażasz zgodę na kontakt zwrotny w sprawie ubezpieczenia.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
